// ── §7 estimate ↔ HubSpot release reconciliation ────────────────────────────
// A turnover's releases are the HubSpot deal's line items (the same rows the
// Print Package's "Release Schedule" shows): each has an id, a name, an amount
// and an `awarded` flag — "Released" when true, "Forecasted" otherwise.
//
// Section 7 is organized by release (2026-09-23): each release has its own
// upload area, and a document uploaded there carries that release's line-item
// id (TurnoverDocuments.HubSpotLineItemID). Rules:
//   • every RELEASED line item needs an "Estimate" whose value equals its
//     amount EXACTLY (to the cent — no rounding allowance);
//   • an Estimate tagged to a release must equal that release's amount;
//   • a forecasted release's area is optional (the anticipated full
//     construction estimate on a partial release goes there);
//   • an Estimate not tied to a release (General area, or uploaded before
//     this change) still counts toward the release whose amount it equals
//     exactly — so turnovers already in progress keep working;
//   • every Estimate must carry a value.
// The header's deal total is the sum of every release, so it is never the
// comparison basis on a multi-release deal.
//
// Pure — no React, no I/O. Used by getSectionMissing / getSectionHealth in
// TurnoverDetail.jsx, the §7 upload areas and the reconciliation table.

export const ESTIMATE_DOC_TYPE = "Estimate";

// "$3,787,184.00" / "3787184" / 3787184 → 3787184; anything else → null.
export function parseMoney(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const cleaned = String(v).replace(/[$,\s]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// Exact comparison in whole cents (avoids floating-point noise, allows none).
export const cents = (n) => Math.round(Number(n) * 100);
export const sameAmount = (a, b) => a !== null && b !== null && cents(a) === cents(b);

export function isReleased(li) {
  return li?.awarded === true || li?.awarded === "true";
}

export const releaseKeyOf = (li, i) => String(li?.id ?? li?.hs_object_id ?? `li-${i}`);

export const fmtMoney = (n) =>
  "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: cents(n) % 100 ? 2 : 0, maximumFractionDigits: 2 });

function docLabel(doc) {
  return doc.FileName || doc.Description || `document #${doc.TurnoverDocumentID}`;
}

export function releasesOf(lineItems = []) {
  return (lineItems || []).map((li, i) => ({
    key: releaseKeyOf(li, i),
    name: li.name || `Line item ${i + 1}`,
    amount: parseMoney(li.amount),
    released: isReleased(li),
    matchedDocs: [],
  }));
}

// Returns {
//   releases:  [{ key, name, amount, released, matchedDocs: [doc] }],
//   estimates: [{ doc, value, release | null, taggedRelease | null, issue | null }],
//   missing:   [string]   — empty means §7 may complete
// }
export function checkEstimateReleases(lineItems, sectionDocs) {
  const releases = releasesOf(lineItems);
  const byKey = new Map(releases.map(r => [r.key, r]));

  const estimates = (sectionDocs || [])
    .filter(d => d.DocType === ESTIMATE_DOC_TYPE)
    .map(doc => {
      const value = parseMoney(doc.DocValue);
      const tag = doc.HubSpotLineItemID ? String(doc.HubSpotLineItemID) : null;
      const taggedRelease = tag ? byKey.get(tag) || null : null;
      if (value === null) return { doc, value, release: null, taggedRelease, issue: "no_value" };
      if (tag && !taggedRelease) return { doc, value, release: null, taggedRelease: null, issue: "unknown_release" };
      if (taggedRelease) {
        if (taggedRelease.amount !== null && sameAmount(value, taggedRelease.amount)) {
          taggedRelease.matchedDocs.push(doc);
          return { doc, value, release: taggedRelease, taggedRelease, issue: null };
        }
        return { doc, value, release: null, taggedRelease, issue: "mismatch" };
      }
      const hit = releases.find(r => r.amount !== null && sameAmount(value, r.amount));
      if (hit) { hit.matchedDocs.push(doc); return { doc, value, release: hit, taggedRelease: null, issue: null }; }
      return { doc, value, release: null, taggedRelease: null, issue: "no_match" };
    });

  const missing = [];
  if (releases.length === 0) {
    missing.push("The HubSpot deal has no releases (line items) — add them in HubSpot, then Refresh on the Cover Sheet");
    return { releases, estimates, missing };
  }
  for (const r of releases) {
    if (r.amount === null) missing.push(`HubSpot release "${r.name}" has no amount`);
  }
  for (const e of estimates) {
    if (e.issue === "no_value") missing.push(`Enter the dollar value on Estimate "${docLabel(e.doc)}"`);
    else if (e.issue === "unknown_release") missing.push(`Estimate "${docLabel(e.doc)}" is tagged to a release no longer on the HubSpot deal — delete and re-upload it under the right release`);
    else if (e.issue === "mismatch") missing.push(`Estimate "${docLabel(e.doc)}" (${fmtMoney(e.value)}) does not equal "${e.taggedRelease.name}" (${fmtMoney(e.taggedRelease.amount)}) — off by ${fmtMoney(Math.abs(e.value - e.taggedRelease.amount))}`);
    else if (e.issue === "no_match") missing.push(`Estimate "${docLabel(e.doc)}" (${fmtMoney(e.value)}) does not equal any HubSpot release`);
  }
  for (const r of releases) {
    if (r.released && r.amount !== null && r.matchedDocs.length === 0) {
      missing.push(`Upload an Estimate for released "${r.name}" (${fmtMoney(r.amount)})`);
    }
  }
  // All releases still forecasted: at least one estimate must still tie out.
  if (!releases.some(r => r.released) && !releases.some(r => r.matchedDocs.length > 0)) {
    missing.push("Upload an Estimate that equals one of the HubSpot releases");
  }
  return { releases, estimates, missing };
}

// Upload areas for §7: one per release, then "General" for everything not
// tied to a release (including documents tagged to a release since removed).
export function estimateDocGroups(lineItems, sectionDocs) {
  const releases = releasesOf(lineItems);
  const keys = new Set(releases.map(r => r.key));
  const groups = releases.map(r => ({
    key: r.key,
    title: r.name,
    subtitle: `${r.amount !== null ? fmtMoney(r.amount) : "No amount"} · ${r.released ? "Released — an Estimate equal to this amount is required" : "Forecasted — optional"}`,
    expectedValue: r.amount,
    uploadMeta: { hubspot_line_item_id: r.key },
    documents: (sectionDocs || []).filter(d => d.HubSpotLineItemID && String(d.HubSpotLineItemID) === r.key),
  }));
  groups.push({
    key: "general",
    title: releases.length ? "General — not tied to a release" : "Documents",
    subtitle: releases.length ? "Budget exports, summaries and anything covering the whole job" : "",
    expectedValue: null,
    uploadMeta: { hubspot_line_item_id: null },
    documents: (sectionDocs || []).filter(d => !d.HubSpotLineItemID || !keys.has(String(d.HubSpotLineItemID))),
    isGeneral: true,
  });
  return groups;
}
